import type { ReactNode } from 'react';
import { Color, Size, asyncStateKinds, t, type AsyncState } from '@bms2/shared';
import { Button } from '../controls';
import { Text } from '../Text';
import styles from './shared.module.css';

/**
 * Loading, empty and failed (piece 4): every List Page, Record Page and Tile
 * renders all three as three different messages, never one blank area. The
 * empty state names the next action; the failed state offers a retry, the
 * treatment Address Verification's failure uses.
 */
export const asyncStatePanelDocumentedStates = asyncStateKinds;

/**
 * 🔒 A ready answer being read again, or whose reread failed (decision 1431).
 * The read hook keeps the last answer drawn while a reload of the same path is
 * in flight and marks it `refreshing`; a reload that fails keeps it beside the
 * error. Both ride on the ready state itself, so every panel draws them without
 * its caller passing anything.
 */
export type RefreshedState<T> = { kind: 'ready'; data: T; refreshing?: true; reloadError?: string };

/** Whether a state is a kept answer being reread, and the reread's failure if it failed. */
export function refreshOf(state: AsyncState<unknown>): { refreshing: boolean; reloadError: string | null } {
  if (state.kind !== 'ready') return { refreshing: false, reloadError: null };
  const held = state as RefreshedState<unknown>;
  return { refreshing: held.refreshing === true, reloadError: typeof held.reloadError === 'string' ? held.reloadError : null };
}

export interface AsyncStatePanelProps<T> {
  state: AsyncState<T>;
  /** What to do next when nothing is here yet; an empty state never says only that it is empty. */
  emptyAction: string;
  onRetry: () => void;
  children: (data: T) => ReactNode;
}

export function AsyncStatePanel<T>({ state, emptyAction, onRetry, children }: AsyncStatePanelProps<T>) {
  switch (state.kind) {
    case 'loading':
      return (
        <div className={styles.asyncPanel} data-async="loading" role="status" aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <Text variant="small">{t('async.loading')}</Text>
        </div>
      );
    case 'empty':
      return (
        <div className={styles.asyncPanel} data-async="empty">
          <Text variant="small">{t('async.empty')}</Text>
          <Text variant="small">{emptyAction}</Text>
        </div>
      );
    case 'failed':
      return (
        <div className={styles.asyncPanel} data-async="failed" role="alert">
          <Text variant="small">{t('async.failed')}</Text>
          <Text variant="caption">{state.error}</Text>
          <Button size={Size.Small} color={Color.Navy} text={[t('async.retry')]} actions={[onRetry]} />
        </div>
      );
    case 'ready': {
      /**
       * 🔒 A reload draws what it holds, never the spinner (decision 1431): the
       * spinner is a first load's alone. The refreshing mark sits in the window's corner
       * out of the flow, so nothing moves while the answer is read again. The
       * three slots stay in one order, so the children are never remounted.
       */
      const { refreshing, reloadError } = refreshOf(state);
      return (
        <>
          {refreshing ? <span className={styles.refreshing} data-async-refreshing="true" role="status" aria-label={t('async.loading')} /> : null}
          {reloadError ? (
            <div className={styles.reloadFailed} data-async-reload-failed="true" role="alert">
              <Text variant="small">{t('async.failed')}</Text>
              <Text variant="caption">{reloadError}</Text>
              <Button size={Size.Small} color={Color.Navy} text={[t('async.retry')]} actions={[onRetry]} />
            </div>
          ) : null}
          {children(state.data)}
        </>
      );
    }
  }
}
